/**
 * PartnerLanding — Public-facing partner inventory page.
 *
 * Lives at /partners/:slug
 * Displays the partner's branding, fleet, and a CTA to book through ACE.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Car, ArrowRight, Globe, MapPin } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const f$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function PartnerLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API}/api/partner-network/public/${slug}`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Partner not found');
        if (!cancelled) setData(d);
      } catch (e) { if (!cancelled) setError(e.message); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">404</p>
        <h1 className="text-3xl font-black mt-2">Partner not found</h1>
        <p className="text-gray-400 mt-2">No partner exists at <code className="text-red-400">/partners/{slug}</code>.</p>
        <Link to="/" className="mt-6 inline-flex items-center text-sm uppercase tracking-widest font-bold underline">← Back to ACE</Link>
      </div>
    );
  }

  const { org, vehicles } = data;
  const brand = org.brand_color || '#dc2626';

  return (
    <div className="min-h-screen bg-black text-white" data-testid="partner-landing">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-gray-900">
        <div
          className="absolute inset-0 opacity-20"
          style={{ background: `radial-gradient(circle at 30% 20%, ${brand}, transparent 60%)` }}
        />
        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
          <p className="text-[10px] uppercase tracking-[0.4em] font-bold" style={{ color: brand }}>
            ACE × {org.partner_name}
          </p>
          <h1 className="text-5xl md:text-6xl font-black tracking-tight mt-3">{org.partner_name}</h1>
          {org.tagline && <p className="text-lg md:text-xl text-gray-300 mt-3 max-w-2xl">{org.tagline}</p>}
          <div className="flex flex-wrap items-center gap-4 mt-6 text-xs uppercase tracking-widest text-gray-400">
            <span className="inline-flex items-center gap-1"><Car className="h-3 w-3" /> {vehicles.length} vehicles</span>
            {org.website && (
              <a href={org.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-white">
                <Globe className="h-3 w-3" /> {org.website.replace(/^https?:\/\//, '')}
              </a>
            )}
            {org.contact_phone && <span className="inline-flex items-center gap-1">{org.contact_phone}</span>}
          </div>
          <Link
            to="/intake"
            className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-lg font-bold uppercase tracking-widest text-sm hover:scale-105 transition-transform"
            style={{ background: brand }}
            data-testid="partner-cta-book"
          >
            Book Through ACE <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Fleet */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight">Available Fleet</h2>
          <p className="text-xs text-gray-500 uppercase tracking-widest">Bookable via ACE</p>
        </div>
        {vehicles.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Car className="h-12 w-12 mx-auto mb-3 text-gray-700" />
            <p className="text-sm">No vehicles attached to this partner yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehicles.map(v => (
              <div
                key={v.vehicle_id}
                className="bg-gray-950 border border-gray-800 rounded-lg p-5 hover:border-gray-600 transition-colors"
                data-testid={`partner-vehicle-${v.vehicle_id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded" style={{ background: `${brand}30`, color: brand }}>
                    {v.tag || v.category}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{v.pax} pax</span>
                </div>
                <h3 className="text-lg font-bold">{v.vehicle_name}</h3>
                <p className="text-xs text-gray-500 mt-1">Min {v.min_hours} hours</p>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">From</p>
                    <p className="text-2xl font-black">{f$(v.retail_hourly)}<span className="text-xs text-gray-400 font-normal">/hr</span></p>
                  </div>
                  <Link
                    to="/intake"
                    className="text-xs uppercase tracking-widest font-bold border border-gray-700 hover:border-white rounded px-3 py-1.5 transition-colors"
                  >
                    Inquire →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Footer attribution */}
      <footer className="border-t border-gray-900 px-6 py-6 text-center text-[10px] uppercase tracking-widest text-gray-600">
        Powered by ACE · <Link to="/" className="hover:text-white">yourautoconcierge.com</Link>
      </footer>
    </div>
  );
}
